import * as Sentry from '@sentry/react';
import React from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import defaultBrandingData from 'assets/json/branding.json';
import { PAGES, REDIRECT_HOME } from 'common/constants';
import * as css from 'common/css';
import * as environment from 'common/environment';
import { PropertyBranding } from 'common/types';
import { isInIframe } from 'common/utility';
import ErrorPage from 'components/ErrorPage';
import Requested from 'components/subscribe/Requested';
import Subscribe from 'components/subscribe/Subscribe';
import Success from 'components/subscribe/Success';
import Verify from 'components/subscribe/Verify';
import AccessPreferences from 'components/subscribe/property/AccessPreferences';
import AccessPreferencesSent from 'components/subscribe/property/AccessPreferencesSent';
import ManagePreferences from 'components/subscribe/property/ManagePreferences';
import PropertySubscribe from 'components/subscribe/property/PropertySubscribe';
import Unsubscribe from 'components/unsubscribe/Unsubscribe';

declare global {
  interface Window {
    EBX: {
      branding: string;
      [key: string]: any;
    };
  }
}

const fallback = <ErrorPage />;

type LoaderProps = {
  children: React.ReactElement<any>;
};

const Loader = ({ children }: LoaderProps) => {
  let brandingData: PropertyBranding;
  if (
    window.EBX.branding === '<$- BRANDING_JSON $>' &&
    environment.getHostname() === 'dev'
  ) {
    brandingData = defaultBrandingData as unknown as PropertyBranding;
  } else {
    brandingData = JSON.parse(window.EBX.branding);
  }
  css.setBranding(brandingData);

  if (brandingData.error) {
    return <>{fallback}</>;
  }

  return (
    <>
      {React.Children.map(children, child =>
        React.cloneElement(child, {
          branding: brandingData,
        })
      )}
    </>
  );
};

const WrappedSubscribe = () => (
  <Loader>
    <Subscribe />
  </Loader>
);

const WrappedSuccess = () => (
  <Loader>
    <Success />
  </Loader>
);

const WrappedUnsubscribe = () => (
  <Loader>
    <Unsubscribe />
  </Loader>
);

const WrappedVerify = () => (
  <Loader>
    <Verify />
  </Loader>
);

const WrappedPropertySubscribe = () => (
  <Loader>
    <PropertySubscribe />
  </Loader>
);

const WrappedManagePreferences = () => (
  <Loader>{React.createElement(ManagePreferences, { branding: null })}</Loader>
);

const WrappedManagePreferencesSuccess = () => (
  <Loader>
    {React.createElement(ManagePreferences, { branding: null, success: true })}
  </Loader>
);

const WrappedAccessPreferences = () => (
  <Loader>
    <AccessPreferences />
  </Loader>
);

const WrappedAccessPreferencesSent = () => (
  <Loader>
    <AccessPreferencesSent />
  </Loader>
);

const HomePage = () => {
  // If we are not in an iframe or if branding undefined redirect
  if (!isInIframe() || window?.EBX?.branding == null) {
    window.location.href = REDIRECT_HOME;
    return null;
  }

  const brandingData = JSON.parse(window.EBX.branding);

  // If the branding data includes a page name, then render the corresponding page
  // Used during testing only
  switch (brandingData?.page) {
    case PAGES.SUBSCRIBE:
      return <WrappedSubscribe />;
    case PAGES.UNSUBSCRIBE:
      return <WrappedUnsubscribe />;
    case PAGES.PROPERTY_SUBSCRIBE:
      return <WrappedPropertySubscribe />;
    case PAGES.VERIFY:
      return <WrappedVerify />;
    case PAGES.SUCCESS:
      return <WrappedSuccess />;
    case PAGES.MANAGE_PREFERENCES:
      return <WrappedManagePreferences />;
    case PAGES.MANAGE_PREFERENCES_SUCCESS:
      return <WrappedManagePreferencesSuccess />;
    case PAGES.ACCESS_PREFERENCES:
      return <WrappedAccessPreferences />;
    case PAGES.ACCESS_PREFERENCES_SENT:
      return <WrappedAccessPreferencesSent />;
    default:
      throw new Error(
        `Page ${brandingData?.page} passed in branding does not match known pages`
      );
  }
};

const Routing = () => (
  <Sentry.ErrorBoundary fallback={fallback}>
    <Router>
      <Switch>
        <Route
          exact
          path="/accesspreferencessent/:entityKey"
          component={WrappedAccessPreferencesSent}
        />
        <Route
          exact
          path="/accesspreferences/:entityKey"
          component={WrappedAccessPreferences}
        />
        <Route
          exact
          path="/manage/:entityKey/success"
          component={WrappedManagePreferencesSuccess}
        />
        <Route
          exact
          path="/manage/:entityKey"
          component={WrappedManagePreferences}
        />
        <Route
          exact
          path="/subscribe/:campaignName/:entityKey"
          component={WrappedSubscribe}
        />
        <Route
          exact
          path="/subscribe/:entityKey"
          component={WrappedPropertySubscribe}
        />
        <Route
          exact
          path="/verify/:campaignName/:entityKey/:userEmail"
          component={WrappedVerify}
        />
        <Route
          exact
          path="/requested/:campaignName/:entityKey/:userEmail"
          component={Requested}
        />
        <Route
          exact
          path="/success/:campaignName/:entityKey/:userEmail"
          component={WrappedSuccess}
        />
        <Route
          exact
          path="/unsubscribe/:entityKey"
          component={WrappedUnsubscribe}
        />
        <Route exact path="/error" component={ErrorPage} />
        <Route path="*" component={HomePage} />
      </Switch>
    </Router>
  </Sentry.ErrorBoundary>
);

export default Routing;
